import { idsHash } from './idsHash';

/**
 * The phone's copy of a projected list (the generators catalogue) and the pure steps that keep it in step with the server's
 * (FIELD-PERF-2). Nothing here does I/O or reads a clock: every time compared is a server time the server sent.
 */
export type ProjectedRow = ReadonlyArray<string | number | boolean | null>;

export interface ProjectedCopy {
  readonly columns: readonly string[];
  /** Sorted by id (code-unit order), the order the server list is turned into documents in. */
  readonly rows: readonly ProjectedRow[];
  /** The server's own read time for this list (`snapshot.asOf`); sent back as `since.asOf`. */
  readonly asOf: string;
  readonly idsHash: string;
  readonly count: number;
  /** The server's fingerprint of the column layout this copy was built under; echoed back in `since`. A delta under another layout is not merged. */
  readonly columnsHash: string;
}

/** What a full answer says about itself (functions/src/forms/types.ts ProjectedSnapshot). */
export interface ProjectedSnapshotWire { asOf?: unknown; idsHash?: unknown; count?: unknown; columnsHash?: unknown }

/** A delta as sent (functions/src/forms/types.ts ProjectedDelta). Unvalidated until applyDelta. */
export interface ProjectedDeltaWire {
  asOf?: unknown; idsHash?: unknown; count?: unknown; columnsHash?: unknown; columns?: unknown; changed?: unknown; removedIds?: unknown;
}

const byId = (a: ProjectedRow, b: ProjectedRow): number => (a[0] as string < (b[0] as string) ? -1 : a[0] === b[0] ? 0 : 1);

function wellFormedRows(rows: unknown, width: number): rows is ProjectedRow[] {
  return Array.isArray(rows) && rows.every((r) => Array.isArray(r) && r.length === width && typeof r[0] === 'string');
}

const isSnapshotShape = (s: ProjectedSnapshotWire | undefined): s is { asOf: string; idsHash: string; count: number; columnsHash: string } =>
  !!s && typeof s.asOf === 'string' && typeof s.idsHash === 'string' && typeof s.count === 'number' && typeof s.columnsHash === 'string';

/**
 * A copy from a FULL answer, or undefined when the answer cannot serve as a base: no snapshot (an older server), a snapshot that
 * does not describe these rows (count or id fingerprint differ), or malformed rows. Undefined never fails the read: the caller
 * simply holds no copy and asks for the full list next time.
 * @param {string[]} columns the answer's columns
 * @param {unknown[]} rows the answer's rows
 * @param {ProjectedSnapshotWire | undefined} snapshot the answer's snapshot
 * @return {ProjectedCopy | undefined} the copy
 */
export function copyFromFull(columns: readonly string[], rows: readonly ProjectedRow[], snapshot: ProjectedSnapshotWire | undefined): ProjectedCopy | undefined {
  if (!isSnapshotShape(snapshot) || !wellFormedRows(rows, columns.length)) return undefined;
  const sorted = [...rows].sort(byId);
  if (sorted.length !== snapshot.count || idsHash(sorted.map((r) => r[0] as string)) !== snapshot.idsHash) return undefined;
  return { columns: [...columns], rows: sorted, asOf: snapshot.asOf, idsHash: snapshot.idsHash, count: snapshot.count, columnsHash: snapshot.columnsHash };
}

/**
 * A copy read back from storage, or undefined when it is not a sound copy. A stored copy is only trusted after the SAME checks a full answer gets
 * (well-formed rows, sorted by id, count and id fingerprint match): a partial write or an older shape must read as "no copy", never throw later.
 * @param {unknown} value whatever the store returned
 * @return {ProjectedCopy | undefined} the same value, typed, if it is sound
 */
export function validateCopy(value: unknown): ProjectedCopy | undefined {
  const c = value as Partial<ProjectedCopy> | null;
  if (!c || typeof c !== 'object' || !Array.isArray(c.columns) || c.columns[0] !== 'id' || !c.columns.every((x) => typeof x === 'string')) return undefined;
  if (!Array.isArray(c.rows) || c.rows.length === 0) return undefined;
  // Rebuilt through the same path as a full answer: rows well-formed, count and id fingerprint match; the result is sorted by id.
  return copyFromFull(c.columns, c.rows as ProjectedRow[], c);
}

export type DeltaOutcome =
  | { kind: 'ok'; copy: ProjectedCopy }
  /** The merged list is not the server's list (hard delete, missed change, stale base): discard the copy and ask for the full list. */
  | { kind: 'mismatch' }
  /** The delta is not a delta (wrong shape or columns): treated like any malformed answer. */
  | { kind: 'malformed' };

/**
 * Merge a delta into a copy WITHOUT touching it (a new copy is returned; the caller replaces its own only on 'ok'). A row in `changed`
 * replaces the row with that id or is added; an id in `removedIds` is dropped. The result is accepted only when its id fingerprint and
 * size equal what the server says its list is now, so whatever a delta cannot see (a hard delete) shows up here as a mismatch.
 * @param {ProjectedCopy} base the copy the request's `since` came from
 * @param {ProjectedDeltaWire} delta the server's delta
 * @return {DeltaOutcome} the merged copy, or why not
 */
export function applyDelta(base: ProjectedCopy, delta: ProjectedDeltaWire): DeltaOutcome {
  try {
    return mergeDelta(base, delta);
  } catch {
    // Nothing in a merge should throw; if a copy or a delta is odd enough to, the copy is not trusted: one full refetch, never a failed read.
    return { kind: 'mismatch' };
  }
}

function mergeDelta(base: ProjectedCopy, delta: ProjectedDeltaWire): DeltaOutcome {
  if (!isSnapshotShape(delta as ProjectedSnapshotWire) || !Array.isArray(delta.columns) || !Array.isArray(delta.removedIds)) return { kind: 'malformed' };
  // The server's column layout is not the one this copy was built under (a deploy added or reordered a column): the copy cannot be merged, but a
  // fresh full list can replace it, so this is a mismatch (one refetch), not a failure.
  const sameColumns = delta.columns.length === base.columns.length && delta.columns.every((c, i) => c === base.columns[i]);
  if (!sameColumns || delta.columnsHash !== base.columnsHash) return { kind: 'mismatch' };
  if (!wellFormedRows(delta.changed, base.columns.length) || !delta.removedIds.every((id) => typeof id === 'string')) return { kind: 'malformed' };
  const merged = new Map<string, ProjectedRow>(base.rows.map((r) => [r[0] as string, r]));
  (delta.removedIds as string[]).forEach((id) => merged.delete(id));
  delta.changed.forEach((r) => merged.set(r[0] as string, r));
  const rows = Array.from(merged.values()).sort(byId);
  const serverHash = delta.idsHash as string;
  const serverCount = delta.count as number;
  if (rows.length !== serverCount || idsHash(rows.map((r) => r[0] as string)) !== serverHash) return { kind: 'mismatch' };
  // Never move backwards: a delta from an instance whose scan is older than the copy keeps the copy's later time.
  const asOf = (delta.asOf as string) > base.asOf ? (delta.asOf as string) : base.asOf;
  return { kind: 'ok', copy: { columns: base.columns, rows, asOf, idsHash: serverHash, count: serverCount, columnsHash: base.columnsHash } };
}

/** The `since` to send for a copy. */
export const sinceOf = (copy: ProjectedCopy): { asOf: string; idsHash: string; count: number; columnsHash: string } => ({
  asOf: copy.asOf, idsHash: copy.idsHash, count: copy.count, columnsHash: copy.columnsHash,
});
